// Criteria are split into `apiFilters` (server-side, sent to `tripsList`) and
// `postFilter` (client-side, applied to each result).
//
// To BROADEN matching, change `apiFilters`. To NARROW matching, change
// `postFilter`. Server-side dedup keys must use only `apiFilters` so multi-leg
// forms can share queries.

import { addressEquals } from './addressEquals';
import { buildLegTimeWindow } from './buildLegTimeWindow';
import type {
  TripsListApiArg,
  TripsListApiResponse,
} from 'src/common/external/bambi-api/tripsApi';
import { tripStatuses } from 'src/common/tripStatuses';

const DUPLICATION_CHECK_IGNORED_STATUSES = ['canceled'] as const;

const STATUS_IN_EXCLUDING_CANCELED = tripStatuses.filter(
  (s) => !DUPLICATION_CHECK_IGNORED_STATUSES.includes(s as 'canceled')
);

const PAGE_SIZE = 100;

export type DuplicateCriteriaInput = {
  passengerId: string;
  orgZone: string;
  excludeTripId: string | undefined;
  leg: {
    timing: { date: string; pickup_time: string; is_will_call: boolean };
    pickupLocation: { address: { value: string } };
    dropoffLocation: { address: { value: string } };
  };
};

// Subset of TripListRead containing only the fields the duplicate-check
// feature reads. Defined narrowly so trip-shaped factory output (TripFactory,
// which produces TripWrite & TripRead) is structurally assignable in tests.
export type CandidateTrip = Pick<
  TripsListApiResponse['results'][number],
  | 'id'
  | 'status'
  | 'scheduled_pickup_at'
  | 'space_type'
  | 'confirmation_code'
  | 'is_will_call'
  | 'pickup'
  | 'dropoff'
>;

export type DuplicateCriteria = {
  apiFilters: TripsListApiArg;
  postFilter: (trip: CandidateTrip) => boolean;
};

export function buildDuplicateCriteria(
  input: DuplicateCriteriaInput
): DuplicateCriteria {
  const { rangeStart, rangeEnd } = buildLegTimeWindow(
    input.leg.timing,
    input.orgZone
  );

  const apiFilters: TripsListApiArg = {
    passengerIn: [input.passengerId],
    rangeStart,
    rangeEnd,
    statusIn: STATUS_IN_EXCLUDING_CANCELED,
    pageSize: PAGE_SIZE,
  };

  const legPickupPlaceId = input.leg.pickupLocation.address.value;
  const legDropoffPlaceId = input.leg.dropoffLocation.address.value;
  const legIsWillCall = input.leg.timing.is_will_call;
  const excludeTripId = input.excludeTripId;

  const postFilter = (trip: CandidateTrip): boolean => {
    if (excludeTripId && trip.id === excludeTripId) return false;
    if (Boolean(trip.is_will_call) !== legIsWillCall) return false;
    if (
      !addressEquals(
        { place_id: legPickupPlaceId },
        { place_id: trip.pickup.place_id }
      )
    )
      return false;
    if (
      !addressEquals(
        { place_id: legDropoffPlaceId },
        { place_id: trip.dropoff.place_id }
      )
    )
      return false;
    return true;
  };

  return { apiFilters, postFilter };
}
